/* The equipment step's loot: anything from the Items and Consumables lists, at any rarity,
   found, bought or won as lucky loot. Taken loot is listed in the sheet's inventory. */

import { CSSProperties, useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { RARITIES, RARITY_COLORS, RARITY_COLORS_LIGHT } from './content/rarity';
import { useDarkMode } from './useDarkMode';
import { Chips, Panel, StepProps, toggle } from './BuilderParts';
import type { Loot } from './catalog';

const LISTS = { Items: 'Item', Consumables: 'Consumable' } as const;

export default function LootPanel({ c, catalog, set }: StepProps) {
  const dark = useDarkMode();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<keyof typeof LISTS>('Items');
  const [rarity, setRarity] = useState('All');
  const [query, setQuery] = useState('');
  const colors = dark ? RARITY_COLORS : RARITY_COLORS_LIGHT;
  const tint = (l: Loot) => ({ '--rarity': colors[l.rarity]?.color }) as CSSProperties;
  const take = (name: string) => set({ loot: toggle(c.loot, name) });
  const taken = c.loot.map(catalog.lootItem).filter((l): l is Loot => !!l);
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = catalog.loot.filter((l) => {
    const text = `${l.name} ${l.type} ${l.text}`.toLowerCase();
    return l.kind === LISTS[list] && (rarity === 'All' || l.rarity === rarity) && words.every((w) => text.includes(w));
  });

  return (
    <Panel title="Loot">
      <p className="cb-hint">Items and consumables from the site's lists, at any rarity: bought, found or lucky loot.</p>
      {taken.length > 0 && (
        <ul className="cb-loot__taken" aria-label="Your loot">
          {taken.map((l) => (
            <li key={l.name} className="cb-loot__chip" style={tint(l)}>
              <span className="cb-loot__gem" aria-hidden="true" />
              {l.name}
              <button type="button" className="cb-loot__remove" aria-label={`Remove ${l.name}`} onClick={() => take(l.name)}>
                <X size={13} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {!open ? (
        <button type="button" className="cb-btn" onClick={() => setOpen(true)}>
          <Plus size={16} aria-hidden="true" />Add loot
        </button>
      ) : (
        <div className="cb-loot">
          <div className="cb-loot__bar">
            <Chips label="Loot list" options={Object.keys(LISTS)} isOn={(o) => o === list} onPick={(o) => setList(o as keyof typeof LISTS)} />
            <input
              type="search"
              className="cb-loot__search"
              aria-label="Search loot"
              placeholder="Search loot"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <Chips label="Rarity" options={['All', ...RARITIES]} isOn={(o) => o === rarity} onPick={setRarity} />
          <ul className="cb-loot__list" aria-label={list}>
            {shown.map((l) => {
              const has = c.loot.includes(l.name);
              return (
                <li key={l.name} className={`cb-loot__row${has ? ' is-taken' : ''}`} style={tint(l)}>
                  <span className="cb-loot__gem" aria-hidden="true" />
                  <span className="cb-loot__body">
                    <span className="cb-loot__name">{l.name}</span>
                    <span className="cb-loot__meta">{l.rarity} · {l.type}</span>
                    <span className="cb-loot__text">{l.text}</span>
                  </span>
                  <button
                    type="button"
                    className={`cb-btn cb-loot__take${has ? '' : ' cb-btn--primary'}`}
                    aria-label={`Take ${l.name}`}
                    aria-pressed={has}
                    onClick={() => take(l.name)}
                  >
                    {has ? <><Check size={15} aria-hidden="true" />Taken</> : 'Take'}
                  </button>
                </li>
              );
            })}
            {!shown.length && <li className="cb-hint">Nothing matches.</li>}
          </ul>
          <button type="button" className="cb-btn" onClick={() => setOpen(false)}>Done</button>
        </div>
      )}
    </Panel>
  );
}
